/**
 * ChatStorageManager - Handles chat data storage and retrieval using Firestore
 * 
 * This module manages:
 * - Storing chat conversations to Firestore when user is authenticated
 * - Retrieving chat history for authenticated users
 * - Managing chat sessions and character interactions
 */

console.log('ChatStorageManager: Starting module initialization...');

// Wait for Firebase to be ready
let auth, db, firebaseModules;
let isFirebaseReady = false;

// Make Firebase functions available globally within this module
let query, collection, where, orderBy, limit, getDocs, addDoc, Timestamp;

// Listen for Firebase ready event
window.addEventListener('firebaseReady', (event) => {
    console.log('ChatStorageManager: Firebase ready event received');
    auth = event.detail.auth;
    db = event.detail.db;
    firebaseModules = event.detail.modules;
    isFirebaseReady = true;
    
    // Extract Firebase functions for direct use
    query = firebaseModules.query;
    collection = firebaseModules.collection;
    where = firebaseModules.where;
    orderBy = firebaseModules.orderBy;
    limit = firebaseModules.limit;
    getDocs = firebaseModules.getDocs;
    addDoc = firebaseModules.addDoc;
    Timestamp = firebaseModules.Timestamp;
    
    console.log('ChatStorageManager: ✓ Firebase services connected');
    console.log('ChatStorageManager: Auth object type:', typeof auth);
    console.log('ChatStorageManager: DB object type:', typeof db);
});

// If Firebase is already ready, use global objects
if (window.firebaseAuth && window.firebaseDB) {
    console.log('ChatStorageManager: Using existing Firebase connection');
    auth = window.firebaseAuth;
    db = window.firebaseDB;
    firebaseModules = window.firebaseModules;
    isFirebaseReady = true;
    
    // Extract Firebase functions for direct use
    query = firebaseModules.query;
    collection = firebaseModules.collection;
    where = firebaseModules.where;
    orderBy = firebaseModules.orderBy;
    limit = firebaseModules.limit;
    getDocs = firebaseModules.getDocs;
    addDoc = firebaseModules.addDoc;
    Timestamp = firebaseModules.Timestamp;
    
    console.log('ChatStorageManager: ✓ Firebase services connected (existing)');
}

export class ChatStorageManager {
    constructor() {
        this.currentUser = null;
        this.isInitialized = false;
        
        // Listen for authentication state changes
        this.initializeAuthListener();
    }

    /**
     * Initialize authentication state listener
     */
    initializeAuthListener() {
        console.log('ChatStorageManager: Setting up authentication listener');
        
        const setupListener = () => {
            if (!auth || !firebaseModules) {
                console.log('ChatStorageManager: Waiting for Firebase...');
                setTimeout(setupListener, 100);
                return;
            }
            
            console.log('ChatStorageManager: Auth object available:', typeof auth);
            console.log('ChatStorageManager: DB object available:', typeof db);
            
            try {
                firebaseModules.onAuthStateChanged(auth, (user) => {
                    this.currentUser = user;
                    this.isInitialized = true;
                    
                    if (user) {
                        console.log('ChatStorageManager: ✓ User authenticated -', user.email);
                        console.log('ChatStorageManager: ✓ Chat storage is now enabled');
                        console.log('ChatStorageManager: User UID:', user.uid);
                    } else {
                        console.log('ChatStorageManager: ⚠️ User not authenticated');
                        console.log('ChatStorageManager: ⚠️ Chat storage is disabled');
                    }
                });
                console.log('ChatStorageManager: ✓ Auth listener setup complete');
            } catch (error) {
                console.error('ChatStorageManager: ✗ Failed to initialize auth listener:', error);
                this.isInitialized = true;
            }
        };
        
        setupListener();
    }

    /**
     * Check if user is authenticated and storage is available
     * @returns {boolean} True if storage is available
     */
    isStorageAvailable() {
        return this.isInitialized && this.currentUser !== null;
    }

    /**
     * Store a chat message to Firestore
     * @param {string} characterName - Name of the character user is chatting with
     * @param {string} userMessage - The user's message
     * @param {string} characterResponse - The character's response
     * @param {string} detectedTone - The tone detected in the response (optional)
     * @returns {Promise<string|null>} Document ID if successful, null if failed
     */
    async storeChatMessage(characterName, userMessage, characterResponse, detectedTone = null) {
        console.log('ChatStorageManager: Attempting to store chat message');
        console.log('ChatStorageManager: Character:', characterName);
        console.log('ChatStorageManager: User message:', userMessage?.substring(0, 50) + '...');
        console.log('ChatStorageManager: Character response:', characterResponse?.substring(0, 50) + '...');
        console.log('ChatStorageManager: Detected tone:', detectedTone);
        console.log('ChatStorageManager: Storage available?', this.isStorageAvailable());
        console.log('ChatStorageManager: Current user:', this.currentUser);
        console.log('ChatStorageManager: Is initialized?', this.isInitialized);

        if (!this.isStorageAvailable()) {
            console.warn('ChatStorageManager: Storage not available - user not authenticated or not initialized');
            return null;
        }

        try {
            const chatData = {
                userId: this.currentUser.uid,
                userEmail: this.currentUser.email,
                characterName: characterName,
                userMessage: userMessage,
                characterResponse: characterResponse,
                detectedTone: detectedTone,
                timestamp: Timestamp.now(),
                sessionId: this.getCurrentSessionId(),
                createdAt: new Date().toISOString()
            };

            console.log('ChatStorageManager: Preparing to save chat data:', chatData);
            
            // Use Firebase v9+ syntax
            const docRef = await addDoc(collection(db, 'chatHistory'), chatData);
            console.log('ChatStorageManager: ✅ Chat message successfully stored with ID:', docRef.id);

            // Scholar's Journal: record a conversation event (legend defaults to active).
            try { window.logJournalActivity?.('chat_message', { meta: { characterName } }); } catch (e) {}
            
            // Test write to verify connection
            console.log('ChatStorageManager: ✅ Firestore write successful - document created in chatHistory collection');
            return docRef.id;

        } catch (error) {
            console.error('ChatStorageManager: Error storing chat message:', error);
            console.error('ChatStorageManager: Error details:', error.message);
            return null;
        }
    }

    /**
     * Retrieve chat history for the current user
     * @param {string} characterName - Filter by character name (optional)
     * @param {number} limitCount - Maximum number of messages to retrieve (default: 50)
     * @returns {Promise<Array>} Array of chat messages
     */
    async getChatHistory(characterName = null, limitCount = 50) {
        if (!this.isStorageAvailable()) {
            console.log('ChatStorageManager: Cannot retrieve history - user not authenticated');
            return [];
        }

        try {
            let q;
            
            if (characterName) {
                // Get chat history for specific character
                q = query(
                    collection(db, 'chatHistory'),
                    where('userId', '==', this.currentUser.uid),
                    where('characterName', '==', characterName),
                    orderBy('timestamp', 'desc'),
                    limit(limitCount)
                );
            } else {
                // Get all chat history for user
                q = query(
                    collection(db, 'chatHistory'),
                    where('userId', '==', this.currentUser.uid),
                    orderBy('timestamp', 'desc'),
                    limit(limitCount)
                );
            }

            const querySnapshot = await getDocs(q);
            const chatHistory = [];

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                chatHistory.push({
                    id: doc.id,
                    ...data,
                    timestamp: data.timestamp.toDate() // Convert Firestore timestamp to Date
                });
            });

            console.log(`ChatStorageManager: Retrieved ${chatHistory.length} chat messages`);
            return chatHistory.reverse(); // Reverse to show oldest first

        } catch (error) {
            console.error('ChatStorageManager: Error retrieving chat history:', error);
            return [];
        }
    }

    /**
     * Get the most recent conversation for a character
     * @param {string} characterName - Name of the character
     * @returns {Promise<Object|null>} Most recent conversation or null if none found
     */
    async getLastConversation(characterName) {
        console.log(`[ChatStorageManager] getLastConversation called for character: "${characterName}"`);
        
        if (!this.isStorageAvailable()) {
            console.log('ChatStorageManager: Cannot get last conversation - user not authenticated');
            return null;
        }

        console.log(`[ChatStorageManager] User authenticated, searching for conversations with ${characterName}`);
        
        try {
            console.log(`[ChatStorageManager] Building query for userId: ${this.currentUser.uid}, characterName: ${characterName}`);
            console.log(`[ChatStorageManager] Database reference:`, db);
            console.log(`[ChatStorageManager] Firebase modules available:`, Object.keys(firebaseModules));
            
            const q = query(
                collection(db, 'chatHistory'),
                where('userId', '==', this.currentUser.uid),
                where('characterName', '==', characterName),
                orderBy('timestamp', 'desc'),
                limit(1)
            );

            console.log(`[ChatStorageManager] Query built successfully, executing...`);
            const querySnapshot = await getDocs(q);
            
            console.log(`[ChatStorageManager] Query executed, found ${querySnapshot.size} conversations`);
            
            if (!querySnapshot.empty) {
                const doc = querySnapshot.docs[0];
                const data = doc.data();
                console.log(`[ChatStorageManager] Found conversation:`, {
                    userMessage: data.userMessage,
                    characterResponse: data.characterResponse?.substring(0, 100) + '...',
                    timestamp: data.timestamp
                });
                
                // Calculate time ago
                const messageDate = data.timestamp.toDate();
                const now = new Date();
                const timeDiff = now - messageDate;
                const daysAgo = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                
                let timeAgo;
                if (daysAgo === 0) {
                    timeAgo = 'today';
                } else if (daysAgo === 1) {
                    timeAgo = 'yesterday';
                } else if (daysAgo < 7) {
                    timeAgo = `${daysAgo} days ago`;
                } else if (daysAgo < 30) {
                    const weeksAgo = Math.floor(daysAgo / 7);
                    timeAgo = weeksAgo === 1 ? '1 week ago' : `${weeksAgo} weeks ago`;
                } else {
                    const monthsAgo = Math.floor(daysAgo / 30);
                    timeAgo = monthsAgo === 1 ? '1 month ago' : `${monthsAgo} months ago`;
                }
                
                const result = {
                    userMessage: data.userMessage,
                    characterResponse: data.characterResponse,
                    detectedTone: data.detectedTone,
                    timestamp: messageDate,
                    timeAgo: timeAgo,
                    topic: this.extractTopic(data.userMessage, data.characterResponse)
                };
                console.log(`[ChatStorageManager] Returning conversation result:`, result);
                return result;
            } else {
                console.log(`[ChatStorageManager] No conversations found for ${characterName}`);
            }
            
            return null;
            
        } catch (error) {
            console.error('ChatStorageManager: Error getting last conversation:', error);
            console.error('ChatStorageManager: Error code:', error.code);
            console.error('ChatStorageManager: Error message:', error.message);
            
            // Check for specific Firebase errors
            if (error.code === 'failed-precondition') {
                console.error('ChatStorageManager: ❌ FAILED-PRECONDITION error - This usually means:');
                console.error('ChatStorageManager: 1. Missing required indexes for compound queries');
                console.error('ChatStorageManager: 2. Firestore security rules are blocking the query');
                console.error('ChatStorageManager: 3. The database is not properly initialized');
                console.error('ChatStorageManager: 4. The collection does not exist or is empty');
                
                // Extract index creation link from error message
                if (error.message && error.message.includes('create it here:')) {
                    const linkMatch = error.message.match(/create it here: (https[^\s]+)/);
                    if (linkMatch) {
                        console.error('ChatStorageManager: 🔗 CREATE REQUIRED INDEX HERE:', linkMatch[1]);
                        console.error('ChatStorageManager: ⬆️ Click the link above to create the required composite index');
                    }
                }
                
                // Try fallback: get recent conversations without character filter, then filter in JavaScript
                console.log('ChatStorageManager: Attempting fallback query without character filter...');
                try {
                    const fallbackQuery = query(
                        collection(db, 'chatHistory'),
                        where('userId', '==', this.currentUser.uid),
                        orderBy('timestamp', 'desc'),
                        limit(10) // Get more to increase chances of finding the character
                    );
                    
                    const fallbackSnapshot = await getDocs(fallbackQuery);
                    console.log(`ChatStorageManager: Fallback query found ${fallbackSnapshot.size} conversations`);
                    
                    // Filter for the specific character in JavaScript
                    for (const doc of fallbackSnapshot.docs) {
                        const data = doc.data();
                        if (data.characterName === characterName) {
                            console.log(`ChatStorageManager: ✓ Found conversation via fallback for ${characterName}`);
                            
                            // Calculate time ago
                            const messageDate = data.timestamp.toDate();
                            const now = new Date();
                            const timeDiff = now - messageDate;
                            const daysAgo = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                            
                            let timeAgo;
                            if (daysAgo === 0) {
                                timeAgo = 'today';
                            } else if (daysAgo === 1) {
                                timeAgo = 'yesterday';
                            } else if (daysAgo < 7) {
                                timeAgo = `${daysAgo} days ago`;
                            } else if (daysAgo < 30) {
                                const weeksAgo = Math.floor(daysAgo / 7);
                                timeAgo = weeksAgo === 1 ? '1 week ago' : `${weeksAgo} weeks ago`;
                            } else {
                                const monthsAgo = Math.floor(daysAgo / 30);
                                timeAgo = monthsAgo === 1 ? '1 month ago' : `${monthsAgo} months ago`;
                            }
                            
                            return {
                                userMessage: data.userMessage,
                                characterResponse: data.characterResponse,
                                detectedTone: data.detectedTone,
                                timestamp: messageDate,
                                timeAgo: timeAgo,
                                topic: this.extractTopic(data.userMessage, data.characterResponse)
                            };
                        }
                    }
                    
                    console.log(`ChatStorageManager: No conversations found for ${characterName} in fallback query`);
                    
                } catch (fallbackError) {
                    console.error('ChatStorageManager: ❌ Fallback query also failed:', fallbackError);
                }
            }
            
            return null;
        }
    }

    /**
     * Extract topic from conversation for memory greeting
     * @param {string} userMessage - User's message
     * @param {string} characterResponse - Character's response
     * @returns {string} Extracted topic
     */
    extractTopic(userMessage, characterResponse) {
        // Simple topic extraction - look for key phrases
        const combinedText = (userMessage + ' ' + characterResponse).toLowerCase();
        
        // Science/Physics topics
        if (combinedText.includes('quantum') || combinedText.includes('physics') || combinedText.includes('experiment')) {
            return 'physics and scientific experiments';
        }
        
        // History topics
        if (combinedText.includes('history') || combinedText.includes('historical') || combinedText.includes('past')) {
            return 'historical events';
        }
        
        // Philosophy topics
        if (combinedText.includes('philosophy') || combinedText.includes('meaning') || combinedText.includes('life')) {
            return 'philosophical questions';
        }
        
        // Biography topics
        if (combinedText.includes('childhood') || combinedText.includes('family') || combinedText.includes('early life')) {
            return 'personal history and early life';
        }
        
        // Career topics
        if (combinedText.includes('work') || combinedText.includes('career') || combinedText.includes('discovery')) {
            return 'career and discoveries';
        }
        
        // Default to general discussion
        return 'general discussion';
    }

    /**
     * Get recent chat sessions grouped by character
     * @param {number} daysBack - Number of days to look back (default: 30)
     * @returns {Promise<Object>} Object with character names as keys and message counts as values
     */
    async getChatSessionSummary(daysBack = 30) {
        if (!this.isStorageAvailable()) {
            return {};
        }

        try {
            const dateThreshold = new Date();
            dateThreshold.setDate(dateThreshold.getDate() - daysBack);

            const q = query(
                collection(db, 'chatHistory'),
                where('userId', '==', this.currentUser.uid),
                where('timestamp', '>=', Timestamp.fromDate(dateThreshold)),
                orderBy('timestamp', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const sessionSummary = {};

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                const characterName = data.characterName;
                
                if (!sessionSummary[characterName]) {
                    sessionSummary[characterName] = {
                        messageCount: 0,
                        lastInteraction: null,
                        toneDistribution: {}
                    };
                }
                
                sessionSummary[characterName].messageCount++;
                
                // Track last interaction
                const messageDate = data.timestamp.toDate();
                if (!sessionSummary[characterName].lastInteraction || 
                    messageDate > sessionSummary[characterName].lastInteraction) {
                    sessionSummary[characterName].lastInteraction = messageDate;
                }
                
                // Track tone distribution
                if (data.detectedTone) {
                    if (!sessionSummary[characterName].toneDistribution[data.detectedTone]) {
                        sessionSummary[characterName].toneDistribution[data.detectedTone] = 0;
                    }
                    sessionSummary[characterName].toneDistribution[data.detectedTone]++;
                }
            });

            console.log('ChatStorageManager: Retrieved session summary for', Object.keys(sessionSummary).length, 'characters');
            return sessionSummary;

        } catch (error) {
            console.error('ChatStorageManager: Error retrieving session summary:', error);
            return {};
        }
    }

    /**
     * Generate a session ID for grouping related chat messages
     * @returns {string} Session ID
     */
    getCurrentSessionId() {
        // Use combination of user ID, date, and random component for session ID
        const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
        const randomComponent = Math.random().toString(36).substr(2, 9);
        return `${this.currentUser?.uid}_${today}_${randomComponent}`;
    }

    /**
     * Delete chat history for a specific character or all history
     * @param {string} characterName - Character name to delete history for (optional)
     * @returns {Promise<boolean>} True if successful
     */
    async deleteChatHistory(characterName = null) {
        if (!this.isStorageAvailable()) {
            console.log('ChatStorageManager: Cannot delete history - user not authenticated');
            return false;
        }

        try {
            let q;
            
            if (characterName) {
                q = query(
                    collection(db, 'chatHistory'),
                    where('userId', '==', this.currentUser.uid),
                    where('characterName', '==', characterName)
                );
            } else {
                q = query(
                    collection(db, 'chatHistory'),
                    where('userId', '==', this.currentUser.uid)
                );
            }

            const querySnapshot = await getDocs(q);
            const deletePromises = [];
            
            querySnapshot.forEach((doc) => {
                deletePromises.push(doc.ref.delete());
            });

            await Promise.all(deletePromises);
            
            const deletedCount = deletePromises.length;
            console.log(`ChatStorageManager: Deleted ${deletedCount} chat messages`);
            return true;

        } catch (error) {
            console.error('ChatStorageManager: Error deleting chat history:', error);
            return false;
        }
    }

    /**
     * Get user's current authentication state
     * @returns {Object|null} Current user object or null
     */
    getCurrentUser() {
        return this.currentUser;
    }

    /**
     * Check if a specific character has chat history
     * @param {string} characterName - Character name to check
     * @returns {Promise<boolean>} True if history exists
     */
    async hasCharacterHistory(characterName) {
        if (!this.isStorageAvailable()) {
            return false;
        }

        try {
            const q = query(
                collection(db, 'chatHistory'),
                where('userId', '==', this.currentUser.uid),
                where('characterName', '==', characterName),
                limit(1)
            );

            const querySnapshot = await getDocs(q);
            return !querySnapshot.empty;

        } catch (error) {
            console.error('ChatStorageManager: Error checking character history:', error);
            return false;
        }
    }
}

// Make ChatStorageManager available globally
window.ChatStorageManager = ChatStorageManager;

// Create a global instance for easy access
window.chatStorageManager = new ChatStorageManager();

console.log('ChatStorageManager: ✓ Module loaded and made available globally');